import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

interface UpdateTemplateResponse {
  data: {
    results: {
      app_id: string;
      device_id: string;
      result: string;
      status_code: number;
    }[];
  };
  success: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PngGeneratorService {

  imageData: string | null = null;
  urlBase = environment.apiUrl;

  constructor(
    private http: HttpClient,

  ) { }

  onError(place: string, error: unknown): void {
    if (error instanceof Error) {
      console.error(place, ":", error.message);
    } else {
      console.error(place, ":", error);
    }
    // this.stopAutoRefresh();
  }

  async generateDynamicTemplate(fullText: string, url: string, device_id: string, id_user: string) {
    const canvasEl = document.createElement('canvas');
    const ctx = canvasEl.getContext('2d');
    if (!ctx)
      throw new Error('ctx non défini');
    const width = 400;
    const height = 600;
    canvasEl.width = width;
    canvasEl.height = height;

    ctx.clearRect(0, 0, width, height);

    // Fond blanc
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);

    const maxLines = url.trim() ? 4 : 10;
    const lines = this.splitTextSmart(fullText, 20, maxLines);
    let fontSize = this.getFontSizeForLines(lines);
    let totalTextHeight = lines.length * fontSize + (lines.length - 1) * 10;
    const qrNeeded = !!url?.trim();
    const qrSize = qrNeeded ? 400 : 0;
    const spacingBelowText = qrNeeded ? 40 : 0;

    const totalContentHeight = totalTextHeight + spacingBelowText + qrSize;

    // Réduction si le tout dépasse la hauteur du canvas
    while (totalContentHeight > height && fontSize > 20) {
      fontSize -= 2;
      totalTextHeight = lines.length * fontSize + (lines.length - 1) * 10;
    }

    ctx.fillStyle = '#000';
    ctx.font = `${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    let y = this.getStartY(height, fontSize, lines.length, qrNeeded ? qrSize + spacingBelowText : 0);

    for (const line of lines) {
      ctx.fillText(line, width / 2, y);
      y += fontSize + 10;
    }

    if (url?.trim()) {
      const qrImg = await this.generateQRCode(url);
      const qrSize = 200;
      const qrDisplayHeight = 420;
      const paddingY = (qrDisplayHeight - qrSize) / 2;

      ctx.fillStyle = '#fff';
      ctx.fillRect((width - qrSize) / 2, y, qrSize, qrDisplayHeight); // fond blanc rectangle QR

      ctx.drawImage(qrImg, (width - qrSize) / 2, y + paddingY - 100, qrSize, qrDisplayHeight);

    }
    //tentative de reduction de taille en passant tout en noir et blanc
    const imageData = ctx.getImageData(0, 0, canvasEl.width, canvasEl.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const avg = (r + g + b) / 3;
      const isWhite = avg > 128;

      data[i] = data[i + 1] = data[i + 2] = isWhite ? 255 : 0; // noir ou blanc
      data[i + 3] = 255; // opacité max
    }

    ctx.putImageData(imageData, 0, 0);

    const imageBase64 = canvasEl.toDataURL('image/png');
    this.imageData = imageBase64;
    this.sendToBackendWithIds(device_id, id_user, imageBase64);

  }

  private splitTextSmart(text: string, maxLen = 25, maxLines = 4): string[] {
    const results: string[] = [];

    const recurse = (chunk: string) => {
      if (results.length >= maxLines || !chunk.trim()) return;

      if (chunk.length <= maxLen) {
        results.push(chunk.trim());
        return;
      }

      const sub = chunk.slice(0, maxLen + 10);
      let splitIndex = -1;

      const lastDot = sub.lastIndexOf(".");
      if (lastDot !== -1 && lastDot <= maxLen) {
        splitIndex = lastDot + 1;
      } else {
        const lastSpace = chunk.lastIndexOf(" ", maxLen);
        splitIndex = lastSpace > 0 ? lastSpace : maxLen;
      }

      const line = chunk.slice(0, splitIndex).trim();
      results.push(line);

      const rest = chunk.slice(splitIndex).trim();
      recurse(rest);
    };

    recurse(text);
    return results.slice(0, maxLines);
  }

  private getFontSizeForLines(lines: string[]): number {
    if (lines.length === 1) {
      const len = lines[0].length;
      if (len <= 8) return 90;
      if (len <= 25) return 45;
      if (len <= 50) return 20;
      return 40;
    }

    const avgLength = lines.reduce((acc, l) => acc + l.length, 0) / lines.length;

    if (lines.length === 2) return avgLength <= 20 ? 50 : 40;
    if (lines.length === 3) return 36;
    return 32;
  }

  private getStartY(height: number, fontSize: number, linesCount: number, reserveBottom = 0): number {
    const totalHeight = linesCount * fontSize + (linesCount - 1) * 10;
    return (height - reserveBottom - totalHeight) / 2 + fontSize * 0.8;
  }

  private async generateQRCode(text: string): Promise<HTMLImageElement> {
    const canvas = document.createElement('canvas');
    const qr = await import('qrcode');
    await qr.toCanvas(canvas, text, { width: 200 });

    const img = new Image();
    img.src = canvas.toDataURL('image/png');
    await new Promise(resolve => (img.onload = resolve));
    return img;
  }

  async postBaseApi<T>(endpoint: string, param: Record<string, unknown> = {}): Promise<T> {
    try {
      const result = await this.http.post<T>(this.urlBase + endpoint, param, {
        headers: { "Content-Type": "application/json" }
      }).toPromise();
      return result as T;
    } catch (error) {
      this.onError(`POST ${endpoint}`, error);
      throw error;
    }
  }

  async sendToBackendWithIds(device_id: string, id_user: string, imageBase64: string) {
    try {
      const body = {
        base_64_image: imageBase64,
        device_id,
        id_user
      };
      const res = await this.postBaseApi<UpdateTemplateResponse>('send-png', body);
      // 🔍 Vérifie la réussite
      if (!res?.success) {
        const errorMessage = (res as any)?.error?.message || 'Erreur inconnue lors de la mise à jour.';
        alert(`❌ ${errorMessage}`);
        return;
      }
      const result = res.data?.results?.[0];
      if (result?.status_code === 404 && result?.result === 'Offline') {
        alert(`❌ Le device "${result.device_id}" n'est pas connecté.`);
        return;
      }
      alert('✅ Image envoyée avec succès.');
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || 'Erreur inconnue.';
      console.error('❌ Erreur lors de l’envoi au backend :', err);
      alert(`❌ ${msg}`);
    }
  }
}
